import {
  Rocket,
  Edit3,
  Sparkles,
  ClipboardCheck,
  Grid3X3,
  Database,
  History,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import type { LanguageCode } from '../types';

// =============================================================================
// TYPES
// =============================================================================

export interface HelpSectionContent {
  title: string;
  summary: string;
  bullets: string[];
  tips?: string[];
}

export interface HelpSectionMeta {
  id: string;
  icon: LucideIcon;
  content: Record<LanguageCode, HelpSectionContent>;
}

// =============================================================================
// PAGE-LEVEL TRANSLATIONS
// =============================================================================

export const HELP_PAGE_STRINGS: Record<LanguageCode, {
  pageTitle: string;
  tocLabel: string;
  tipsLabel: string;
}> = {
  'eng': {
    pageTitle: 'User Guide',
    tocLabel: 'Contents',
    tipsLabel: 'Tips',
  },
  'fa-AF': {
    pageTitle: 'راهنمای کاربر',
    tocLabel: 'فهرست',
    tipsLabel: 'نکات',
  },
  'ps-AF': {
    pageTitle: 'د کارونکي لارښود',
    tocLabel: 'فهرست',
    tipsLabel: 'لارښوونې',
  },
};

// =============================================================================
// SECTIONS
// =============================================================================

export const HELP_SECTIONS: HelpSectionMeta[] = [
  // ── 1. Getting Started ──────────────────────────────────────────────────
  {
    id: 'getting_started',
    icon: Rocket,
    content: {
      'eng': {
        title: 'Getting Started',
        summary: 'What the tool does and how the workflow is organized.',
        bullets: [
          'This is the TOLOnews AI Writing Tool — it helps journalists write, review, and publish stories across TV, web, and social platforms.',
          'The workflow is TV-first: you start with a TV story in Dari or Pashto, then the system generates web and social derivatives automatically.',
          'Each story group has one canonical (original) story and multiple derivatives (translations, web versions, social posts).',
          'Stories go through a pipeline: Draft → AI Review → Submit to Editor → Approved.',
          'English content is web-only — there is no English TV broadcast.',
        ],
        tips: [
          'The Writing Tool page is your main workspace — you can create, edit, and review stories all in one place.',
        ],
      },
      'fa-AF': {
        title: 'شروع کار',
        summary: 'ابزار چه کاری انجام می‌دهد و گردش کار چگونه سازماندهی شده است.',
        bullets: [
          'این ابزار نوشتن هوشمند تلویزیون طلوع‌نیوز است — به ژورنالیستان کمک می‌کند تا مطالب را بنویسند، بازبینی و منتشر کنند.',
          'گردش کار تلویزیون‌محور است: ابتدا خبر تلویزیونی به دری یا پشتو نوشته می‌شود، سپس سیستم نسخه‌های وب و شبکه‌های اجتماعی را خودکار تولید می‌کند.',
          'هر گروه خبری یک خبر اصلی (کانونیکال) و چندین نسخه مشتق (ترجمه‌ها، نسخه‌های وب، پست‌های اجتماعی) دارد.',
          'خبرها از مراحل زیر عبور می‌کنند: پیش‌نویس ← بازبینی هوشمند ← ارسال به سردبیر ← تایید.',
          'محتوای انگلیسی فقط برای وب است — نشرات تلویزیونی انگلیسی وجود ندارد.',
        ],
        tips: [
          'صفحه «ابزار نوشتن» فضای کاری اصلی شماست — می‌توانید مطالب را ایجاد، ویرایش و بازبینی کنید.',
        ],
      },
      'ps-AF': {
        title: 'د کار پیل',
        summary: 'دا وسیله څه کار کوي او د کار بهیر څنګه تنظیم شوی.',
        bullets: [
          'دا د طلوع‌نیوز هوښیار لیکلو وسیله ده — ژورنالیستانو سره مرسته کوي چې خبرونه ولیکي، بیاکتنه وکړي او خپاره یې کړي.',
          'د کار بهیر د تلویزیون لومړیتوب لري: لومړی د تلویزیون خبر په دري یا پښتو لیکل کیږي، بیا سیسټم د ویب او ټولنیزو شبکو نسخې په اتوماتیک ډول جوړوي.',
          'هر خبري ډله یو اصلي (کانونیکال) خبر او ډیری مشتق نسخې (ژباړې، د ویب نسخې، ټولنیزې پوسټونه) لري.',
          'خبرونه د دې مرحلو څخه تیریږي: مسوده ← هوښیار بیاکتنه ← سردبیر ته لیږل ← تایید.',
          'انګلیسي منځپانګه یوازې د ویب لپاره ده — انګلیسي تلویزیوني خپرونه شتون نلري.',
        ],
        tips: [
          'د «لیکلو وسیله» پاڼه ستاسو اصلي کاري ځای دی — تاسو کولی شئ خبرونه جوړ کړئ، سمون ورکړئ او بیاکتنه یې وکړئ.',
        ],
      },
    },
  },

  // ── 2. Creating & Editing ───────────────────────────────────────────────
  {
    id: 'creating_editing',
    icon: Edit3,
    content: {
      'eng': {
        title: 'Creating & Editing Stories',
        summary: 'How to create new stories, edit content, and use the checkout system.',
        bullets: [
          'Click "New Story" to start. Choose the language (Dari, Pashto, or English) and format (TV or Web).',
          'TV stories have three fields: Slug (editorial identifier), Anchor Intro, and Script. Web stories have Headline, Deck, and Body.',
          'Press Ctrl+S (or Cmd+S on Mac) to save your work at any time.',
          'The checkout system prevents conflicts: when you open a story, it locks for other users. They see a "Checked out" banner.',
          'If you forget to close a story, the lock expires automatically after 3 minutes of inactivity.',
          'Use the Scratchpad for personal notes and the Editorial Notes field for team communication.',
          'If you paste content in a different language than selected, the system will warn you before saving.',
        ],
        tips: [
          'The slug must match your ENPS rundown — it is not sent to AI and won\'t be modified.',
          'Save frequently! Auto-save is not enabled.',
        ],
      },
      'fa-AF': {
        title: 'ایجاد و ویرایش مطالب',
        summary: 'چگونه مطلب جدید بسازید، ویرایش کنید و از سیستم قفل استفاده کنید.',
        bullets: [
          'برای شروع «خبر جدید» را کلیک کنید. زبان (دری، پشتو یا انگلیسی) و قالب (تلویزیون یا وب) را انتخاب کنید.',
          'مطالب تلویزیونی سه بخش دارند: سلاگ (شناسه تحریری)، مقدمه گوینده و متن اصلی. مطالب وب شامل تیتر، خلاصه و متن هستند.',
          'برای ذخیره کار خود در هر زمان Ctrl+S (یا Cmd+S در مک) را فشار دهید.',
          'سیستم قفل از تداخل جلوگیری می‌کند: وقتی خبری را باز می‌کنید، برای دیگران قفل می‌شود.',
          'اگر فراموش کنید خبر را ببندید، قفل پس از ۳ دقیقه بی‌فعالیت خودکار منقضی می‌شود.',
          'از یادداشت‌های شخصی برای نکات خود و از یادداشت‌های تحریری برای ارتباط با تیم استفاده کنید.',
          'اگر محتوایی به زبان متفاوت از زبان انتخاب‌شده بچسبانید، سیستم قبل از ذخیره هشدار می‌دهد.',
        ],
        tips: [
          'سلاگ باید با راندان ENPS مطابقت داشته باشد — به هوش مصنوعی ارسال نمی‌شود.',
          'مرتب ذخیره کنید! ذخیره خودکار فعال نیست.',
        ],
      },
      'ps-AF': {
        title: 'د خبرونو جوړول او سمون',
        summary: 'څنګه نوی خبر جوړ کړئ، سمون ورکړئ او د قفل سیسټم وکاروئ.',
        bullets: [
          'د پیل لپاره «نوی خبر» کلیک وکړئ. ژبه (دري، پښتو یا انګلیسي) او بڼه (تلویزیون یا ویب) غوره کړئ.',
          'تلویزیوني خبرونه درې برخې لري: سلاګ (تحریري پیژندونکی)، د ګوینده مقدمه او سکرپټ. ویب خبرونه سرلیک، لنډیز او متن لري.',
          'د خپل کار ساتلو لپاره هر وخت Ctrl+S (یا په مک کې Cmd+S) کېکاږئ.',
          'د قفل سیسټم د ټکر مخنیوی کوي: کله چې تاسو خبر خلاص کړئ، د نورو لپاره بندیږي.',
          'که چیرې خبر بندول هیر کړئ، قفل د ۳ دقیقو بې فعالیت وروسته په اتوماتیک ډول پای ته رسیږي.',
          'د شخصي یادښتونو لپاره سکرچ‌پډ او د ټیم اړیکو لپاره تحریري یادښتونه وکاروئ.',
          'که چیرې د ټاکل شوې ژبې څخه مختلف ژبې منځپانګه ولګوئ، سیسټم به د ساتلو دمخه خبرداری ورکړي.',
        ],
        tips: [
          'سلاګ باید د ENPS راندان سره سمون ولري — هوښیار ماشین ته نه لیږل کیږي.',
          'په مکرر ډول خوندي کړئ! اتوماتیک خوندي کول فعال نه دي.',
        ],
      },
    },
  },

  // ── 3. AI Review ────────────────────────────────────────────────────────
  {
    id: 'ai_review',
    icon: Sparkles,
    content: {
      'eng': {
        title: 'AI Review',
        summary: 'How the AI review pipeline works and what the critics check.',
        bullets: [
          'Click "AI Review" on any canonical (original) story to start the review pipeline. It takes about 30-60 seconds.',
          'First, the Journalist AI polishes your content — fixing grammar, structure, and format while preserving your meaning.',
          'Then 4 independent critics run in parallel, each with a different focus:',
          'Style Critic — checks grammar, demonyms (country-specific terms), broadcast format, and terminology.',
          'Journalism Critic — checks attribution, sourcing, logical consistency, completeness, and balance.',
          'Fact Checker — verifies background facts against web sources.',
          'Deep Check — searches international news sources for verification.',
          'Finally, the Curator consolidates all critic feedback into the top 10 most important issues.',
          'AI Review is only available on canonical stories (not derivatives) and only when the story is saved.',
        ],
        tips: [
          'You don\'t have to accept every suggestion — use your editorial judgment.',
          'The review changes your story status from "Draft" to "Reviewed".',
        ],
      },
      'fa-AF': {
        title: 'بازبینی هوشمند',
        summary: 'خط لوله بازبینی هوشمند چگونه کار می‌کند و منتقدان چه چیزی را بررسی می‌کنند.',
        bullets: [
          'روی «بازبینی هوشمند» در هر خبر اصلی کلیک کنید. حدود ۳۰ تا ۶۰ ثانیه طول می‌کشد.',
          'ابتدا ژورنالیست هوشمند محتوای شما را صیقل می‌دهد — دستور زبان، ساختار و قالب را اصلاح می‌کند.',
          'سپس ۴ منتقد مستقل به صورت همزمان اجرا می‌شوند:',
          'منتقد سبک — دستور زبان، اصطلاحات جغرافیایی، قالب نشراتی و اصطلاحات تخصصی.',
          'منتقد ژورنالیزم — ارجاع‌دهی، منابع، انسجام منطقی، کامل بودن و توازن.',
          'راستی‌آزما — واقعیت‌های پس‌زمینه را با منابع آنلاین تایید می‌کند.',
          'بررسی عمیق — منابع خبری بین‌المللی را برای تایید جستجو می‌کند.',
          'در نهایت، مدیر محتوا تمام بازخوردها را در ۱۰ مورد مهم‌تر خلاصه می‌کند.',
          'بازبینی هوشمند فقط روی مطالب اصلی (نه مشتقات) و فقط وقتی ذخیره شده باشد کار می‌کند.',
        ],
        tips: [
          'مجبور نیستید هر پیشنهادی را بپذیرید — از قضاوت تحریری خود استفاده کنید.',
          'بازبینی، وضعیت خبر را از «پیش‌نویس» به «بازبینی‌شده» تغییر می‌دهد.',
        ],
      },
      'ps-AF': {
        title: 'هوښیار بیاکتنه',
        summary: 'د هوښیار بیاکتنې پایپ لاین څنګه کار کوي او انتقادکونکي څه چک کوي.',
        bullets: [
          'په هر اصلي خبر کې «هوښیار بیاکتنه» کلیک وکړئ. شاوخوا ۳۰ تر ۶۰ ثانیو پورې وخت نیسي.',
          'لومړی، هوښیار ژورنالیست ستاسو منځپانګه صیقلوي — ګرامر، جوړښت او بڼه سموي.',
          'بیا ۴ خپلواک انتقادکونکي په یو وخت کې چلیږي:',
          'د سټایل انتقادکونکی — ګرامر، جغرافیایي اصطلاحات، خپرونې بڼه او مسلکي اصطلاحات.',
          'د ژورنالیزم انتقادکونکی — ارجاع ورکول، سرچینې، منطقي سازش، بشپړتیا او توازن.',
          'حقیقت تصدیقوونکی — پس‌منظر حقایق د آنلاین سرچینو سره تاییدوي.',
          'ژوره بررسي — د نړیوالو خبري سرچینو کې د تایید لپاره پلټنه کوي.',
          'په پای کې، کیوریټر ټول نظرونه په ۱۰ تر ټولو مهمو مسلو کې لنډوي.',
          'هوښیار بیاکتنه یوازې په اصلي خبرونو (نه مشتقاتو) کې شتون لري.',
        ],
        tips: [
          'اړتیا نشته چې هره وړاندیز ومنئ — خپل تحریري قضاوت وکاروئ.',
          'بیاکتنه د خبر حالت د «مسوده» څخه «بیاکتل شوی» ته بدلوي.',
        ],
      },
    },
  },

  // ── 4. Editor Approval ──────────────────────────────────────────────────
  {
    id: 'editor_approval',
    icon: ClipboardCheck,
    content: {
      'eng': {
        title: 'Editor Approval',
        summary: 'How to submit stories for editor review and the approval process.',
        bullets: [
          'After AI Review, your story status changes to "Reviewed". You can now submit it to an editor.',
          'Click "Submit to Editor" to send the story for editorial approval. The status changes to "Pending Review".',
          'An editor (who is not the story author) can then Approve or Send Back the story.',
          'Self-approval is not allowed — you cannot approve your own stories, even if you have editor permissions.',
          'If sent back, the editor adds rejection notes explaining what needs to change. The story returns to "Draft".',
          'When approved, the story is locked and derivatives (web versions, translations, social posts) are generated automatically.',
          'The Editor Desk page gives editors an overview of all stories pending their review.',
        ],
        tips: [
          'Read the rejection notes carefully before resubmitting — they appear at the top of the editor.',
          'After approval, you can still unlock a story if urgent corrections are needed.',
        ],
      },
      'fa-AF': {
        title: 'تایید سردبیر',
        summary: 'چگونه مطالب را برای بازبینی سردبیر ارسال کنید و فرآیند تایید.',
        bullets: [
          'پس از بازبینی هوشمند، وضعیت خبر به «بازبینی‌شده» تغییر می‌کند. حالا می‌توانید آن را به سردبیر ارسال کنید.',
          '«ارسال به سردبیر» را کلیک کنید. وضعیت به «در انتظار بازبینی» تغییر می‌کند.',
          'یک سردبیر (که نویسنده خبر نباشد) می‌تواند خبر را تایید یا برگرداند.',
          'خودتاییدی مجاز نیست — حتی اگر دسترسی سردبیر دارید، نمی‌توانید مطالب خودتان را تایید کنید.',
          'اگر برگردانده شود، سردبیر یادداشت‌های رد را اضافه می‌کند. خبر به «پیش‌نویس» برمی‌گردد.',
          'پس از تایید، خبر قفل می‌شود و نسخه‌های مشتق خودکار تولید می‌شوند.',
          'صفحه «میز سردبیر» نمای کلی از تمام مطالب در انتظار بازبینی را نشان می‌دهد.',
        ],
        tips: [
          'یادداشت‌های رد را با دقت بخوانید — در بالای ویرایشگر نمایش داده می‌شوند.',
          'پس از تایید، هنوز می‌توانید خبر را باز کنید اگر اصلاحات فوری لازم باشد.',
        ],
      },
      'ps-AF': {
        title: 'د سردبیر تایید',
        summary: 'څنګه خبرونه د سردبیر بیاکتنې لپاره واستوئ او د تایید پروسه.',
        bullets: [
          'د هوښیار بیاکتنې وروسته، د خبر حالت «بیاکتل شوی» ته بدلیږي. اوس تاسو کولی شئ سردبیر ته یې واستوئ.',
          '«سردبیر ته لیږل» کلیک وکړئ. حالت «د بیاکتنې په تمه» ته بدلیږي.',
          'یو سردبیر (چې د خبر لیکوال نه وي) کولی شي خبر تایید یا بیرته واستوي.',
          'ځان تایید کول اجازه نلري — حتی که د سردبیر اجازه لرئ، خپل خبرونه نشئ تایید کولی.',
          'که بیرته واستول شي، سردبیر د ردولو یادښتونه اضافه کوي. خبر «مسودې» ته راستنیږي.',
          'د تایید وروسته، خبر بندیږي او مشتق نسخې په اتوماتیک ډول جوړیږي.',
          'د «سردبیر میز» پاڼه سردبیرانو ته د ټولو خبرونو چې بیاکتنې ته اړتیا لري عمومي لید ورکوي.',
        ],
        tips: [
          'د ردولو یادښتونه په دقت سره ولولئ — د سمونګر په سر کې ښکاره کیږي.',
          'د تایید وروسته، تاسو لا هم کولی شئ خبر خلاص کړئ که عاجلو سمونونو ته اړتیا وي.',
        ],
      },
    },
  },

  // ── 5. Derivatives & Translation ────────────────────────────────────────
  {
    id: 'derivatives',
    icon: Grid3X3,
    content: {
      'eng': {
        title: 'Derivatives & Translation',
        summary: 'How the Story Grid works and how derivatives are generated.',
        bullets: [
          'The Story Grid shows all versions of a story across platforms (TV, Web) and languages (Dari, Pashto, English).',
          'When a TV story is approved, the system automatically creates derivative drafts for all valid combinations.',
          'Generation happens in two phases: first all web versions (parallel), then TV translations and social posts.',
          'Grid cells show status: Pending (amber) → Generating (purple) → Draft (ready to edit).',
          'You can click any empty or pending cell to manually trigger generation for that specific derivative.',
          'Cross-language web articles are first rewritten from the Facts Stream, then translated to the target language.',
          'Social posts only use verified facts — because social media spreads fast and corrections are difficult.',
        ],
        tips: [
          'English TV cells will always be empty — TOLOnews does not broadcast in English.',
          'If generation fails, click the cell again to retry.',
        ],
      },
      'fa-AF': {
        title: 'مشتقات و ترجمه',
        summary: 'شبکه خبری چگونه کار می‌کند و مشتقات چگونه تولید می‌شوند.',
        bullets: [
          'شبکه خبری تمام نسخه‌های یک خبر را در پلتفرم‌ها (تلویزیون، وب) و زبان‌ها (دری، پشتو، انگلیسی) نشان می‌دهد.',
          'وقتی خبر تلویزیونی تایید می‌شود، سیستم خودکار پیش‌نویس‌های مشتق را برای تمام ترکیب‌های معتبر ایجاد می‌کند.',
          'تولید در دو مرحله انجام می‌شود: ابتدا تمام نسخه‌های وب، سپس ترجمه‌های تلویزیونی و پست‌های اجتماعی.',
          'خانه‌های شبکه وضعیت را نشان می‌دهند: در انتظار (نارنجی) ← در حال تولید (بنفش) ← پیش‌نویس.',
          'می‌توانید هر خانه خالی یا در انتظار را کلیک کنید تا تولید آن مشتق خاص شروع شود.',
          'مقالات وب بین‌زبانی ابتدا از جریان واقعیت‌ها بازنویسی و سپس به زبان مقصد ترجمه می‌شوند.',
          'پست‌های اجتماعی فقط از واقعیت‌های تایید شده استفاده می‌کنند.',
        ],
        tips: [
          'خانه‌های تلویزیون انگلیسی همیشه خالی خواهند بود — تلویزیون طلوع‌نیوز به انگلیسی نشرات ندارد.',
          'اگر تولید ناموفق باشد، دوباره خانه را کلیک کنید.',
        ],
      },
      'ps-AF': {
        title: 'مشتقات او ژباړه',
        summary: 'د خبرونو شبکه څنګه کار کوي او مشتقات څنګه جوړیږي.',
        bullets: [
          'د خبرونو شبکه د یو خبر ټولې نسخې په پلتفرمونو (تلویزیون، ویب) او ژبو (دري، پښتو، انګلیسي) کې ښیي.',
          'کله چې تلویزیوني خبر تایید شي، سیسټم په اتوماتیک ډول د ټولو معتبرو ترکیبونو لپاره مشتق مسودې جوړوي.',
          'تولید په دوه مرحلو کې ترسره کیږي: لومړی ټولې ویب نسخې، بیا تلویزیوني ژباړې او ټولنیزې پوسټونه.',
          'د شبکې حجرې حالت ښیي: په تمه (نارنجي) ← جوړیدل (بنفش) ← مسوده.',
          'تاسو کولی شئ هره خالي یا په تمه حجره کلیک وکړئ ترڅو د هغه مشتق تولید پیل شي.',
          'د ژبو تر منځ ویب مقالې لومړی د حقایقو جریان څخه بیا لیکل کیږي، بیا مقصد ژبې ته ژباړل کیږي.',
          'ټولنیزې پوسټونه یوازې تایید شوي حقایق کاروي.',
        ],
        tips: [
          'انګلیسي تلویزیوني حجرې به تل خالي وي — طلوع‌نیوز په انګلیسي نشرات نلري.',
          'که تولید ناکام شي، حجره بیا کلیک وکړئ.',
        ],
      },
    },
  },

  // ── 6. Facts Stream ─────────────────────────────────────────────────────
  {
    id: 'facts_stream',
    icon: Database,
    content: {
      'eng': {
        title: 'Facts Stream',
        summary: 'How facts are extracted, verified, and used across the system.',
        bullets: [
          'During AI Review, the Journalist AI extracts structured facts from your story: claims, quotes, statistics, events, and background information.',
          'Each fact has a confidence level shown by color:',
          'Green (Verified) — confirmed by multiple sources or critics.',
          'Blue (Attributed) — extracted from the story, default starting level.',
          'Amber (Unverified) — a critic could not confirm this fact.',
          'Red (Disputed) — a critic found contradicting information.',
          'Facts that no critic challenges are automatically promoted from Attributed to Verified.',
          'The Facts Stream panel (right side of the editor) shows all facts for the current story group.',
          'Web rewriter uses ALL facts to create richer web articles. Social posts use ONLY verified facts.',
        ],
        tips: [
          'Disputed facts need your attention — check the source and update your story if needed.',
          'Facts are shared across all stories in the same group (translations see the same facts).',
        ],
      },
      'fa-AF': {
        title: 'جریان واقعیت‌ها',
        summary: 'واقعیت‌ها چگونه استخراج، تایید و در سیستم استفاده می‌شوند.',
        bullets: [
          'در بازبینی هوشمند، ژورنالیست هوشمند واقعیت‌های ساختاریافته را استخراج می‌کند: ادعاها، نقل قول‌ها، آمار، رویدادها و اطلاعات پس‌زمینه.',
          'هر واقعیت سطح اطمینان رنگی دارد:',
          'سبز (تایید شده) — توسط چندین منبع یا منتقد تایید شده.',
          'آبی (منسوب) — از خبر استخراج شده، سطح پیش‌فرض.',
          'نارنجی (تایید نشده) — منتقد نتوانست این واقعیت را تایید کند.',
          'قرمز (مورد اختلاف) — منتقد اطلاعات متناقض یافته.',
          'واقعیت‌هایی که هیچ منتقدی به چالش نکشد، خودکار از «منسوب» به «تایید شده» ارتقا می‌یابند.',
          'پنل جریان واقعیت‌ها (سمت راست ویرایشگر) تمام واقعیت‌های گروه خبری فعلی را نشان می‌دهد.',
          'بازنویس وب از تمام واقعیت‌ها و پست‌های اجتماعی فقط از واقعیت‌های تایید شده استفاده می‌کنند.',
        ],
        tips: [
          'واقعیت‌های مورد اختلاف نیاز به توجه شما دارند — منبع را بررسی کنید.',
          'واقعیت‌ها در تمام مطالب یک گروه مشترک هستند.',
        ],
      },
      'ps-AF': {
        title: 'د حقایقو جریان',
        summary: 'حقایق څنګه استخراج، تایید او په سیسټم کې کارول کیږي.',
        bullets: [
          'د هوښیار بیاکتنې پرمهال، هوښیار ژورنالیست جوړښت لرونکي حقایق استخراجوي: ادعاګانې، نقل قولونه، شمیرنې، پیښې او شالید معلومات.',
          'هر حقیقت د رنګ له مخې د باور کچه لري:',
          'شنه (تایید شوی) — د څو سرچینو یا انتقادکونکو لخوا تایید شوی.',
          'آبي (منسوب) — له خبر څخه استخراج شوی، لومړنۍ کچه.',
          'نارنجي (تایید نشوی) — انتقادکونکی دا حقیقت تایید نشو کولی.',
          'سور (مورد منازعه) — انتقادکونکي متناقض معلومات وموندل.',
          'هغه حقایق چې هیڅ انتقادکونکی یې نه ننګوي، په اتوماتیک ډول «منسوب» څخه «تایید شوي» ته لوړیږي.',
          'د حقایقو پنل (د سمونګر ښي خوا) د اوسني خبري ډلې ټول حقایق ښیي.',
          'د ویب بیا لیکونکی ټول حقایق او ټولنیزې پوسټونه یوازې تایید شوي حقایق کاروي.',
        ],
        tips: [
          'مورد منازعې حقایق ستاسو پام ته اړتیا لري — سرچینه وګورئ.',
          'حقایق د یوې ډلې په ټولو خبرونو کې شریک دي.',
        ],
      },
    },
  },

  // ── 7. Version History ──────────────────────────────────────────────────
  {
    id: 'version_history',
    icon: History,
    content: {
      'eng': {
        title: 'Version History',
        summary: 'How to view past versions of a story and restore them.',
        bullets: [
          'Click the History icon (clock) in the editor toolbar to open the Version History sidebar.',
          'Every time you save changes to headline, deck, body, or TV fields, a snapshot is automatically captured.',
          'The timeline shows who made each change and when, with badges indicating which fields changed.',
          'Click any revision to see a detailed diff — additions in green, removals in red.',
          'Diffs are RTL-aware for Dari and Pashto content.',
          'Click "Restore this version" to load the old content into the editor. Review it before saving.',
          'Restoring does not save automatically — you can compare and decide whether to keep it.',
        ],
        tips: [
          'Version History and the AI Review panel cannot be open at the same time.',
          'Each save creates a new revision — save often to maintain a detailed history.',
        ],
      },
      'fa-AF': {
        title: 'تاریخچه نسخه‌ها',
        summary: 'چگونه نسخه‌های قبلی خبر را ببینید و بازیابی کنید.',
        bullets: [
          'آیکون تاریخچه (ساعت) را در نوار ابزار ویرایشگر کلیک کنید تا نوار کناری باز شود.',
          'هر بار که تغییرات تیتر، خلاصه، متن یا فیلدهای تلویزیونی را ذخیره کنید، یک عکس فوری خودکار ثبت می‌شود.',
          'خط زمانی نشان می‌دهد چه کسی و چه زمانی تغییر داده، با نشانه‌هایی که فیلدهای تغییریافته را مشخص می‌کند.',
          'هر نسخه را کلیک کنید تا تفاوت‌ها را ببینید — اضافات به رنگ سبز، حذفیات به رنگ قرمز.',
          'تفاوت‌ها برای محتوای دری و پشتو از راست به چپ نمایش داده می‌شوند.',
          '«بازیابی این نسخه» را کلیک کنید تا محتوای قبلی در ویرایشگر بارگذاری شود.',
          'بازیابی خودکار ذخیره نمی‌کند — می‌توانید مقایسه کنید و تصمیم بگیرید.',
        ],
        tips: [
          'تاریخچه نسخه‌ها و پنل بازبینی هوشمند نمی‌توانند همزمان باز باشند.',
          'هر ذخیره یک نسخه جدید ایجاد می‌کند — مرتب ذخیره کنید.',
        ],
      },
      'ps-AF': {
        title: 'د نسخو تاریخچه',
        summary: 'څنګه د خبر پخوانۍ نسخې وګورئ او بیرته راوړئ.',
        bullets: [
          'د سمونګر په اوزار پټې کې د تاریخچې آیکون (ساعت) کلیک وکړئ ترڅو اړخیزه پټه خلاصه شي.',
          'هر کله چې سرلیک، لنډیز، متن یا تلویزیوني ساحو کې بدلونونه خوندي کړئ، یو عکس په اتوماتیک ډول اخیستل کیږي.',
          'مهال ویش ښیي چا او کله بدلون کړی، د بدل شویو ساحو نښو سره.',
          'هره نسخه کلیک وکړئ ترڅو توپیرونه وګورئ — زیاتونه شنه، ایستل شوي سور.',
          'توپیرونه د دري او پښتو منځپانګو لپاره له ښي څخه کیڼ اړخ ته ښودل کیږي.',
          '«د دې نسخې بیرته راوړل» کلیک وکړئ ترڅو پخوانۍ منځپانګه سمونګر کې بار شي.',
          'بیرته راوړل په اتوماتیک ډول نه خوندي کیږي — تاسو کولی شئ پرتله وکړئ او پریکړه وکړئ.',
        ],
        tips: [
          'د نسخو تاریخچه او هوښیار بیاکتنه پنل نشي کولی په یو وخت کې خلاص وي.',
          'هر خوندي کول یوه نوې نسخه جوړوي — په مکرر ډول خوندي کړئ.',
        ],
      },
    },
  },

  // ── 8. Tips & Shortcuts ─────────────────────────────────────────────────
  {
    id: 'tips_shortcuts',
    icon: Zap,
    content: {
      'eng': {
        title: 'Tips & Shortcuts',
        summary: 'Keyboard shortcuts, production markers, and helpful tips.',
        bullets: [
          'Ctrl+S (Cmd+S on Mac) — Save the current story.',
          'TV scripts must preserve production markers exactly as written:',
          'کلپ: — clip/soundbite (never modify, includes verbatim speech)',
          'گرافیک: — graphic card / lower-third',
          'صدا و تصویر — B-roll / natural sound',
          '«»  — guillemets (direct quotes, never change text inside)',
          '... / ..... — section breaks',
          'The duration indicator at the bottom of TV stories shows estimated broadcast time based on word count.',
          'Colors mean: green = within target, yellow = slightly off, red = significantly over/under.',
          'Use dark mode (toggle in sidebar) for comfortable editing in low-light environments.',
        ],
        tips: [
          'If the duration is red, try shortening the script — the anchor intro and script timings are shown separately.',
          'The "Check Again" button in the checkout banner refreshes the lock status without reloading the page.',
        ],
      },
      'fa-AF': {
        title: 'نکات و میانبرها',
        summary: 'میانبرهای صفحه‌کلید، نشانه‌های تولید و نکات مفید.',
        bullets: [
          'Ctrl+S (Cmd+S در مک) — ذخیره خبر فعلی.',
          'متن‌های تلویزیونی باید نشانه‌های تولید را دقیقا حفظ کنند:',
          'کلپ: — کلیپ/صدای مصاحبه (هرگز تغییر ندهید)',
          'گرافیک: — کارت گرافیکی / زیرنویس',
          'صدا و تصویر — تصاویر پس‌زمینه / صدای طبیعی',
          '«»  — گیومه (نقل قول مستقیم، متن داخل را تغییر ندهید)',
          '... / ..... — جداکننده بخش‌ها',
          'نشانگر مدت زمان در پایین مطالب تلویزیونی، زمان تخمینی نشرات را نشان می‌دهد.',
          'رنگ‌ها: سبز = در محدوده هدف، زرد = کمی خارج، قرمز = بیش از حد کم/زیاد.',
          'از حالت تاریک (دکمه در نوار کناری) برای ویرایش راحت در محیط کم‌نور استفاده کنید.',
        ],
        tips: [
          'اگر مدت زمان قرمز است، متن را کوتاه‌تر کنید.',
          'دکمه «بررسی مجدد» در بنر قفل، وضعیت قفل را بدون بارگذاری مجدد صفحه تازه می‌کند.',
        ],
      },
      'ps-AF': {
        title: 'لارښوونې او لنډ لارې',
        summary: 'د کیبورډ لنډ لارې، د تولید نښې او ګټورې لارښوونې.',
        bullets: [
          'Ctrl+S (په مک کې Cmd+S) — اوسنی خبر خوندي کول.',
          'تلویزیوني سکرپټونه باید د تولید نښې دقیقا وساتي:',
          'کلپ: — کلیپ/غږیز ټوټه (هیڅکله مه یې بدلوئ)',
          'گرافیک: — ګرافیک کارت / لاندیز لیکنه',
          'صدا و تصویر — شاليد انځورونه / طبیعي غږ',
          '«»  — ګیومې (مستقیم نقل قول، دننه متن مه بدلوئ)',
          '... / ..... — د برخو جلاکونکي',
          'د تلویزیوني خبرونو په پای کې د موده نښه د ټکو شمیر پر بنسټ اټکل شوی خپرونې وخت ښیي.',
          'رنګونه: شنه = هدف ته نږدې، ژیړ = لږ واټن، سور = ډیر کم/زیات.',
          'د تیارې حالت (د اړخیزې پټې تڼۍ) د کم رڼا چاپیریال کې د آرام سمون لپاره وکاروئ.',
        ],
        tips: [
          'که موده سره وي، سکرپټ لنډ کړئ.',
          'د قفل بنر کې «بیا ګوره» تڼۍ د قفل حالت پرته د پاڼې بیا راوړلو تازه کوي.',
        ],
      },
    },
  },
];
